import { Stepper } from "@shared/components/Stepper";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import React, { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@shared/ui/use-toast";
import { normalizeDate } from "@shared/utils/dateUtils";
import { SelectConsumersStep } from "./stepComponents/selectConsumers/SelectConsumersStep";
import { RequestDetailsStep } from "./stepComponents/requestDetails/RequestDetailsStep";
import { ReviewSubmitStep } from "./stepComponents/reviewSubmit/ReviewSubmitStep";
import { useCreateBulkRequest } from "../../../hooks";

const BulkCreateReconnectionRequest: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();
  const storageKey = `bulk-reconnection-request-progress-${remoteUtilityId}`;

  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("Bulk Reconnection");

  const { mutate: createBulkRequest, isPending } = useCreateBulkRequest();

  const [selectedCount, setSelectedCount] = React.useState(0);

  // Function to sync count from localStorage
  const syncSelectedCount = useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const data = JSON.parse(raw);
        const count = data.stepData?.[0]?.selectedConsumers?.length || 0;
        setSelectedCount(count);
      }
    } catch (e) {
      console.error("Failed to sync selected count:", e);
    }
  }, [storageKey]);

  // Initial sync
  React.useEffect(() => {
    syncSelectedCount();
  }, [syncSelectedCount]);

  const handleComplete = useCallback(
    (stepDataArray: any[]) => {
      // Collect all data from the stepper's internal state
      const selectionData = stepDataArray[0] || {};
      const detailsData = stepDataArray[1] || {};
      const paymentData = stepDataArray[2] || {};

      console.log("paymentData", paymentData);

      const selectedConsumers = selectionData.selectedConsumers || [];

      if (selectedConsumers.length < 2) {
        toast({
          title: "Validation Error",
          description: "At least 2 consumers must be selected to proceed.",
          variant: "destructive",
        });
        return;
      }

      const reconnectionFee = Number(detailsData.reconnectionFee) || 0;
      const calculatedGrandTotal = reconnectionFee * selectedConsumers.length;

      const finalPayload = {
        remoteUtilityId: Number(remoteUtilityId),
        requestType: "Reconnection",
        consumerIds: selectedConsumers.map((c: any) => Number(c.id)),
        reason: detailsData.reasonLabel,
        preferredDate: detailsData.scheduledDate
          ? normalizeDate(detailsData.scheduledDate)
          : null,
        preferredTimeSlot: Number(detailsData.timeSlot) || 0,
        additionalNotes: detailsData.consumerRemark || "",
        priority: Number(detailsData.priority),
        source: 0,
        payment_data: {
          payment_received_status: 0,
          amount: calculatedGrandTotal,
          payment_mode: paymentData.payment_method,
          payment_date: normalizeDate(
            paymentData.paymentDate ||
              paymentData.chequeDate ||
              paymentData.onlineDate,
          ),
          remote_utility_id: Number(remoteUtilityId),
          status: "CREDIT",
          payment_pay_type: 5,
          source: 0,
          extra_data: {
            ...paymentData,
          },
        },
      };

      console.log("Submitting bulk reconnection request:", finalPayload);

      createBulkRequest(finalPayload, {
        onSuccess: () => {
          stopTracking?.();
          navigate("/cx/reconnections");
          toast({
            title: "Success",
            description: "Bulk reconnection request created successfully.",
          });
        },
      });
    },
    [remoteUtilityId, createBulkRequest, stopTracking, navigate],
  );

  // Memoize the steps array to prevent infinite re-renders
  const steps = useMemo(
    () => [
      {
        title: "Select Consumers",
        slug: "select-consumers",
        component: (
          <SelectConsumersStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            logTimeUntilStep={logTimeUntilStep}
            onSelectionChange={(count) => setSelectedCount(count)}
          />
        ),
        optional: false,
        infoTitle: "Select Consumers Help",
        infoDescription: `Select consumers for bulk reconnection. ${selectedCount} consumers selected.`,
      },
      {
        title: "Request Details",
        slug: "request-details",
        component: (
          <RequestDetailsStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            logTimeUntilStep={logTimeUntilStep}
          />
        ),
        optional: false,
        infoTitle: "Request Details Help",
        infoDescription: `These details will apply to all ${selectedCount} selected consumers.`,
      },
      {
        title: "Review & Submit",
        slug: "review-submit",
        component: (
          <ReviewSubmitStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            logTimeUntilStep={logTimeUntilStep}
            stopTracking={stopTracking}
            isSubmitting={isPending}
          />
        ),
        optional: false,
        infoTitle: "Review & Submit Help",
        infoDescription:
          "Review all your bulk reconnection details and fee summaries before submitting the request.",
      },
    ],
    [
      remoteUtilityId,
      storageKey,
      logTimeUntilStep,
      stopTracking,
      isPending,
      selectedCount,
    ],
  );

  return (
    <div className="flex flex-col gap-5">
      <Stepper
        steps={steps}
        onStepChange={() => {
          syncSelectedCount();
        }}
        onStepSave={async (stepIndex, stepData, isDirty) => {
          // Storage is handled at component level or by Stepper internally
        }}
        enableApiSave={false}
        saveOnStepChange={false}
        trackDirtyState={false}
        autoSave={false}
        persistProgress={false}
        allowSkip={false}
        storageKey={storageKey}
        enableRouting={true}
        onValidation={undefined}
        onComplete={handleComplete}
        showDraftResume={false}
      />
    </div>
  );
};

export default BulkCreateReconnectionRequest;
